import { useState, useEffect, useMemo } from 'react';
import { Layout, Card, Spin, Alert, Typography, Modal, Button, Space, Input, Popconfirm, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { LikeOutlined, LikeFilled, MessageOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './index.css';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function Home({
  posts: propsPosts,
  loading,
  error,
  loggedInUser,
  onLogout,
  onDeletePost,
  onEditPost
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const navigate = useNavigate();

  // 1. 统一数据处理：缓存+过滤有效文章（确保ID存在）
  const posts = useMemo(() => {
    const processed = Array.isArray(propsPosts) ? [...propsPosts] : [];
    // 强制ID为字符串，确保统一性
    return processed.map(post => ({ ...post, id: String(post.id || `post_${Date.now()}`) }));
  }, [propsPosts]);

  // 2. 过滤有效文章（必须有ID和标题）
  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      return post && post.id && post.title; // 过滤无ID/无标题的无效文章
    });
  }, [posts]);

  // 调试日志
  useEffect(() => {
    console.log('【Home有效文章】', filteredPosts.map(p => ({ id: p.id, title: p.title })));
  }, [filteredPosts]);

  // 点赞状态（持久化）
  const [likeStates, setLikeStates] = useState([]);
  useEffect(() => {
    const savedLikes = JSON.parse(localStorage.getItem('postLikes') || '[]');
    setLikeStates(
      filteredPosts.map(post => {
        const savedLike = savedLikes.find(like => like.postId === post.id);
        return {
          postId: post.id,
          count: savedLike?.count || post.likeCount || 0,
          isLiked: savedLike?.isLiked || false
        };
      })
    );
  }, [filteredPosts]);

  // 评论状态（持久化）
  const [commentStates, setCommentStates] = useState([]);
  const [newComment, setNewComment] = useState('');
  useEffect(() => {
    const savedComments = JSON.parse(localStorage.getItem('postComments') || '[]');
    setCommentStates(
      filteredPosts.map(post => {
        const savedComment = savedComments.find(cmt => cmt.postId === post.id);
        return {
          postId: post.id,
          comments: savedComment?.comments || post.comments || []
        };
      })
    );
  }, [filteredPosts]);

  // 业务方法
  const handleLike = (postId, e) => {
    e.stopPropagation();
    setLikeStates(prev => {
      const newStates = prev.map(state => {
        if (state.postId === postId) {
          const newIsLiked = !state.isLiked;
          const newCount = newIsLiked ? state.count + 1 : Math.max(0, state.count - 1);
          return { ...state, isLiked: newIsLiked, count: newCount };
        }
        return state;
      });
      localStorage.setItem('postLikes', JSON.stringify(newStates));
      return newStates;
    });
  };

  const getLikeState = (postId) => {
    return likeStates.find(item => item.postId === postId) || { count: 0, isLiked: false };
  };

  const getPostComments = (postId) => {
    return commentStates.find(item => item.postId === postId)?.comments || [];
  };

  const handleSubmitComment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newComment.trim() || !currentPost) return;

    const newCommentObj = {
      id: Date.now(),
      content: newComment.trim(),
      author: loggedInUser?.username || '匿名用户',
      time: new Date().toLocaleString()
    };

    setCommentStates(prev => {
      const newStates = prev.map(state => {
        if (state.postId === currentPost.id) {
          const updatedComments = [...state.comments, newCommentObj];
          return { ...state, comments: updatedComments };
        }
        return state;
      });
      localStorage.setItem('postComments', JSON.stringify(newStates));
      return newStates;
    });
    setNewComment('');
    message.success('评论发布成功！');
  };

  const handleOpenModal = (post, e) => {
    e.stopPropagation();
    setCurrentPost(post);
    setModalVisible(true);
    setNewComment('');
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setCurrentPost(null);
  };

  const handleEditClick = (postId, e) => {
    e.stopPropagation();
    if (!loggedInUser) {
      message.warning('请先登录后再编辑文章');
      navigate('/auth');
      return;
    }
    navigate(`/edit/${postId}`);
  };

  const handleDeleteConfirm = (postId, e) => {
    e.stopPropagation();
    if (!loggedInUser) {
      message.warning('请先登录后再删除文章');
      navigate('/auth');
      return;
    }

    try {
      // 调用父组件删除方法（同步更新propsPosts）
      onDeletePost(postId);
      // 同步删除本地存储数据
      const savedLikes = JSON.parse(localStorage.getItem('postLikes') || '[]');
      localStorage.setItem('postLikes', JSON.stringify(savedLikes.filter(like => like.postId !== postId)));
      const savedComments = JSON.parse(localStorage.getItem('postComments') || '[]');
      localStorage.setItem('postComments', JSON.stringify(savedComments.filter(cmt => cmt.postId !== postId)));
      message.success('文章删除成功！');
      setModalVisible(false);
    } catch (err) {
      message.error('删除失败：' + err.message);
    }
  };

  // 加载/错误状态
  if (loading) {
    return (
      <div className="loading-container" style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" tip="加载文章中..." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="error-container" style={{ padding: '50px' }}>
        <Alert
          message="加载失败"
          description={error.message || '获取文章列表失败，请刷新重试'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <Layout className="home-layout">
      <Header loggedInUser={loggedInUser} onLogout={onLogout} />

      <Content className="home-content" style={{ padding: '24px', minHeight: 'calc(100vh - 120px)' }}>
        <div className="post-list-container">
          <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
            共 {filteredPosts.length} 篇文章
          </div>

          {filteredPosts.length > 0 ? (
            <div className="post-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
              {filteredPosts.map((post) => {
                const { count: likeCount, isLiked } = getLikeState(post.id);
                const commentCount = getPostComments(post.id).length;
                const canOperate = loggedInUser && String(post.authorId) === String(loggedInUser.id);

                return (
                  <Card
                    key={post.id}
                    className="post-card"
                    hoverable
                    onClick={(e) => handleOpenModal(post, e)}
                    extra={canOperate ? (
                      <Space size="small" className="post-card-actions">
                        <Button
                          type="text"
                          icon={<EditOutlined className="edit-icon" />}
                          onClick={(e) => handleEditClick(post.id, e)}
                          title="编辑文章"
                          className="action-btn"
                        />
                        <Popconfirm
                          title="确定要删除这篇文章吗？"
                          okText="删除"
                          cancelText="取消"
                          onConfirm={(e) => handleDeleteConfirm(post.id, e)}
                          onCancel={(e) => e.stopPropagation()}
                          placement="top"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            icon={<DeleteOutlined className="delete-icon" />}
                            onClick={(e) => e.stopPropagation()}
                            title="删除文章"
                            className="action-btn"
                          />
                        </Popconfirm>
                      </Space>
                    ) : null}
                    style={{ height: '100%', cursor: 'pointer' }}
                  >
                    <div className="post-tags" style={{ marginBottom: '8px' }}>
                      {post.tags?.length ? (
                        post.tags.map((tag) => (
                          <span
                            key={`tag-${post.id}-${tag}`}
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '4px',
                              marginRight: '8px',
                              fontSize: '12px'
                            }}
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span style={{ fontSize: '12px', color: '#999' }}>无标签</span>
                      )}
                    </div>

                    <Title level={4} className="post-title" style={{ margin: '8px 0' }}>
                      <Link
                        to={`/post/${post.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#1890ff', textDecoration: 'none', display: 'block' }}
                      >
                        {post.title}
                      </Link>
                    </Title>

                    <Paragraph className="post-summary" ellipsis={{ rows: 2 }} style={{ margin: '8px 0' }}>
                      {post.summary || post.content?.slice(0, 100) || '无摘要'}
                    </Paragraph>

                    <div className="post-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                      <Text className="post-time" style={{ fontSize: '12px', color: '#999' }}>
                        {post.createTime ? new Date(post.createTime).toLocaleDateString() : '未知时间'}
                      </Text>

                      <Space size="small" className="post-stats">
                        <Button
                          type="text"
                          icon={isLiked ? <LikeFilled style={{ color: '#f5222d' }} /> : <LikeOutlined />}
                          onClick={(e) => handleLike(post.id, e)}
                          size="small"
                        >
                          {likeCount > 0 ? `${likeCount} 赞` : '赞'}
                        </Button>

                        <Button
                          type="text"
                          icon={<MessageOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(post, e);
                          }}
                          size="small"
                        >
                          {commentCount > 0 ? `${commentCount} 评论` : '评论'}
                        </Button>
                      </Space>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="empty-post-container" style={{ textAlign: 'center', padding: '100px 0' }}>
              <Text className="empty-icon" style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📝</Text>
              <Text className="empty-title" style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }}>还没有文章哦～</Text>
              <Text className="empty-desc" style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '24px' }}>点击下方按钮开始创作你的第一篇博客吧！</Text>
              <Button
                type="primary"
                onClick={() => navigate('/write')}
                className="create-post-btn"
              >
                立即写文章
              </Button>
            </div>
          )}
        </div>
      </Content>

      <Footer />

      {/* 文章模态框 */}
      <Modal
        title={currentPost?.title || '文章详情'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        destroyOnClose={true}
        maskClosable={true}
        className="post-modal"
      >
        {currentPost && (
          <div className="modal-content" style={{ padding: '16px 0' }}>
            <div className="modal-tags" style={{ marginBottom: '16px' }}>
              {currentPost.tags?.length ? (
                currentPost.tags.map((tag) => (
                  <span
                    key={`modal-tag-${currentPost.id}-${tag}`}
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      marginRight: '8px',
                      fontSize: '12px'
                    }}
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span style={{ fontSize: '12px', color: '#999' }}>无标签</span>
              )}
            </div>

            <div className="modal-meta" style={{ marginBottom: '16px', fontSize: '12px', color: '#999' }}>
              <Text className="modal-time" style={{ display: 'block', marginBottom: '4px' }}>
                发布时间：{currentPost.createTime ? new Date(currentPost.createTime).toLocaleString() : '未知时间'}
              </Text>
              {currentPost.author && (
                <Text className="modal-author">
                  作者：{currentPost.author}
                </Text>
              )}
            </div>

            <div className="modal-content-text" style={{ marginBottom: '24px', lineHeight: '1.6' }}>
              {currentPost.content || '暂无内容'}
            </div>

            {/* 评论区 */}
            <div className="comment-section">
              <Title level={4} className="comment-title" style={{ marginBottom: '16px' }}>
                评论区 ({getPostComments(currentPost.id).length})
              </Title>

              <div className="comment-list" style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '16px' }}>
                {getPostComments(currentPost.id).length > 0 ? (
                  getPostComments(currentPost.id).map((comment) => (
                    <div
                      key={`comment-${comment.id}`}
                      style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}
                    >
                      <div className="comment-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text className="comment-author" style={{ fontWeight: 'bold' }}>{comment.author}</Text>
                        <Text className="comment-time" style={{ fontSize: '12px', color: '#999' }}>{comment.time}</Text>
                      </div>
                      <div className="comment-content">
                        {comment.content}
                      </div>
                    </div>
                  ))
                ) : (
                  <Text style={{ fontSize: '14px', color: '#999', textAlign: 'center', display: 'block', padding: '24px' }}>暂无评论，快来抢沙发～</Text>
                )}
              </div>

              <form onSubmit={handleSubmitComment} className="comment-form">
                <TextArea
                  placeholder="写下你的评论..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  maxLength={500}
                  showCount
                  style={{ marginBottom: '16px' }}
                />
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!newComment.trim()}
                >
                  提交评论
                </Button>
              </form>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}